/**
 * Free self-serve onboarding trigger for the Lead Gen Tools landing.
 *
 * The public landing CTAs ("Get my free embed") need to kick the visitor
 * into the same free self-serve path the Free pricing card uses:
 *   signed out -> stash a free pricing intent + Google OAuth
 *   signed in  -> bootstrap the lead-gen agency, then route into the tools.
 *
 * Decoupled from React state via a window CustomEvent (exactly like
 * openEmbedInquiry) so any CTA deep in the landing tree can trigger the
 * flow without prop drilling. The page root installs the listener via
 * useAgencySelfServeListener() and owns navigation, since it already
 * lives inside the Router.
 *
 * Lives in its own file (no component exports) so Vite Fast Refresh stays
 * happy on the landing module.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { stashPricingIntent } from './pricingIntent';

const EVENT = 'start-agency-self-serve';

/**
 * Fire-and-forget: pop the visitor into the free self-serve flow. `source`
 * is carried for funnel attribution (matches the openEmbedInquiry source
 * convention: "hero", "final-cta", "pricing-lead-gen").
 */
export function startAgencySelfServeFree(source: string) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { source } }));
}

/**
 * Page-root listener that performs the actual OAuth / bootstrap / navigate.
 * Mounted once on the landing page; mirrors the embed-inquiry listener that
 * already lives there.
 */
export function useAgencySelfServeListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const onStart = async (e: Event) => {
      const ce = e as CustomEvent<{ source?: string }>;
      const source = ce.detail?.source || 'unknown';

      // Conversion event for GTM so marketing can attribute self-serve
      // starts the same way embed inquiries are tracked.
      try {
        (window as { dataLayer?: unknown[] }).dataLayer =
          (window as { dataLayer?: unknown[] }).dataLayer || [];
        (window as { dataLayer?: unknown[] }).dataLayer!.push({
          event: 'agency_self_serve_start',
          form_source: source,
          plan: 'free',
        });
      } catch {
        /* no-op */
      }

      const { data } = await supabase.auth.getSession();
      const signedIn = !!data.session?.user;

      if (!signedIn) {
        // Stash the free intent so AuthProvider routes correctly on return,
        // then hand off to Google OAuth. Same scopes the Free pricing card
        // requests so the GMB-connected onboarding works downstream.
        stashPricingIntent({ kind: 'free' });
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/fly-app`,
            queryParams: { access_type: 'offline', prompt: 'select_account' },
            scopes:
              'email profile https://www.googleapis.com/auth/business.manage',
          },
        });
        if (error) console.error('[agencySelfServe] Google sign-in failed:', error);
        return;
      }

      // Already signed in: ensure a lead-gen agency exists (bootstrap is
      // idempotent and returns the existing agency for repeat callers),
      // then drop them on the tools page. Mirrors the Free card's route.
      // intent='free' so bootstrap keeps the auto-provisioned 7-day trial
      // — this CTA is the "get my free embed" landing path, the user
      // explicitly asked for the free tier.
      try {
        await supabase.functions.invoke('agency-public-signup-bootstrap', {
          body: { intent: 'free' },
        });
      } catch (err) {
        console.warn(
          '[agencySelfServe] bootstrap failed; AuthProvider will retry:',
          err,
        );
      }
      navigate('/fly-app/lead-gen-tools?welcome=free');
    };

    window.addEventListener(EVENT, onStart);
    return () => window.removeEventListener(EVENT, onStart);
  }, [navigate]);
}
